import { IConfirmTimeSlotRequest, ISendInviteRequest } from '../../interfaces';
import { createMultiPartRequest, createRequest } from '../helpers/api_utils';
import {
  AllApplicantRequest,
  ApplicantEditRequest,
  ApplicantRequest,
  InterviewDetails,
} from '../types/InterviewTypes';
import { apiUrl } from './api_url';

const apiEndpoint = apiUrl + '/smart-interview';
export interface ITimeSlot {
  startTime: number;
  endTime: number;
  isBooked: boolean;
}
export interface IInterviewRound {
  id?: string;
  title: string;
  jobOpeningId: string;
  host: string;
  panelists: string[];
  timeSlots: ITimeSlot[];
  schedulingTemplateHtml: string;
  schedulingTemplateJson: string;
  meetingDetailsTemplateHtml: string;
  meetingDetailsTemplateJson: string;
  meetingDuration: number;
  meetingLink?: string;
  meetingType?: string;
  meetingDetails?: string;
  description?: string;
  location?: string;
}
export async function createInterviewRound(body: IInterviewRound) {
  return fetch(
    createRequest(apiEndpoint, 'POST', 'application/json', true, body),
  );
}
export async function updateInterviewRound(
  interviewId: string,
  body: IInterviewRound,
) {
  return fetch(
    createRequest(
      `${apiEndpoint}/${interviewId}`,
      'PUT',
      'application/json',
      true,
      body,
    ),
  );
}

export async function sentInterviewInvitation(
  applicants: InterviewDetails[],
  interviewRoundId: string,
) {
  const body = {
    applicants: applicants,
  };
  return fetch(
    createRequest(
      `${apiEndpoint}/${interviewRoundId}/applicants`,
      'POST',
      'application/json',
      true,
      body,
    ),
  );
}
export async function getApplicantCandidates(
  body: ApplicantRequest,
  page: number = 1,
  limit: number = 10,
  interviewRoundId: string,
) {
  const queryParams = new URLSearchParams({
    page: page.toString(),
    limit: limit.toString(),
  });

  const url = `${apiEndpoint}/${interviewRoundId}/interview-applicants?${queryParams.toString()}`;

  return fetch(createRequest(url, 'POST', 'application/json', true, body));
}
export async function editInterviewApplicant(
  body: ApplicantEditRequest,
  interviewApplicantId: string,
  interviewRoundId: string,
): Promise<Response> {
  const url = `${apiEndpoint}/${interviewRoundId}/applicants/${interviewApplicantId}`;
  return fetch(
    createRequest(
      url,
      'PUT',
      'application/json',
      true, // includeCredentials
      body,
    ),
  );
}
export async function deleteInterviewApplicant(
  interviewApplicantId: string,
  interviewRoundId: string,
): Promise<Response> {
  const url = `${apiEndpoint}/${interviewRoundId}/applicants/${interviewApplicantId}`;
  return fetch(
    createRequest(
      url,
      'DELETE',
      'application/json',
      true,
      // no body for DELETE
    ),
  );
}
export async function getInterviewApplicant(
  interviewApplicantId: string,
  interviewRoundId: string,
): Promise<Response> {
  const url = `${apiEndpoint}/${interviewRoundId}/applicant/${interviewApplicantId}`;
  return fetch(createRequest(url, 'GET', 'application/json', true));
}
export async function getInvitedApplicants(
  page: number = 1,
  limit: number = 10,
  interviewRoundId: string,
) {
  const queryParams = new URLSearchParams({
    page: page.toString(),
    limit: limit.toString(),
  });

  const url = `${apiEndpoint}/${interviewRoundId}/applicants?${queryParams.toString()}`;

  return fetch(createRequest(url, 'GET', 'application/json', true));
}
export async function uploadInterviewAudio(
  file: File,
  interviewRoundId: string,
  applicantId: string,
) {
  let formData = new FormData();
  formData.append('file', file);

  return fetch(
    createMultiPartRequest(
      `${apiEndpoint}/${interviewRoundId}/applicants/${applicantId}/audio`,
      'POST',
      true,
      formData,
    ),
  );
}
export async function addPanelistNotes(
  interviewRoundId: string,
  applicantId: string,
  notes: string,
) {
  const token = localStorage.getItem('token') || '';

  try {
    const response = await fetch(
      `${apiEndpoint}/${interviewRoundId}/applicants/${applicantId}/note`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ notes }),
      },
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);

      throw new Error(
        errorData?.message ||
          `Failed to save note (Status: ${response.status}). Please try again.`,
      );
    }

    return response.json();
  } catch (error) {
    console.error('Error adding panelist notes:', error);
    throw error;
  }
}

export async function interviewRoundResults(
  interviewRoundId: string,
  page: number = 1,
  limit: number = 10,
) {
  const queryParams = new URLSearchParams({
    page: page.toString(),
    limit: limit.toString(),
  });
  const url = `${apiEndpoint}/${interviewRoundId}/applicants/result?${queryParams.toString()}`;

  return fetch(createRequest(url, 'GET', 'application/json', true));
}

export async function interviewApplicantResults(
  interviewRoundId: string,
  applicantId: string,
) {
  return fetch(
    createRequest(
      apiEndpoint + `/${interviewRoundId}/applicant/${applicantId}`,
      'GET',
      'application/json',
      true,
    ),
  );
}
export async function getInterviewRoundSlots(
  interviewRoundId: string,
  authToken: string,
): Promise<Response> {
  return fetch(`${apiEndpoint}/${interviewRoundId}/candidate/interview-round`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${authToken}`,
    },
  });
}

export async function getInterviewRoundCandidateDetails(
  interviewRoundId: string,
  authToken: string,
): Promise<Response> {
  return fetch(`${apiEndpoint}/${interviewRoundId}/candidate/me`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${authToken}`,
    },
  });
}
export async function confirmTimeSlot(
  body: IConfirmTimeSlotRequest,
  interviewRoundId: string,
  authToken: string,
): Promise<Response> {
  return fetch(`${apiEndpoint}/${interviewRoundId}/schedule`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${authToken}`,
    },
    body: JSON.stringify(body),
  });
}
export async function getOverviewDetails(interviewRoundId: string) {
  return fetch(
    createRequest(
      apiEndpoint + `/${interviewRoundId}/overview`,
      'GET',
      'application/json',
      true,
    ),
  );
}

export async function getInterviewApplicants(
  body: ApplicantRequest,
  page: number = 1,
  limit: number = 10,
  interviewRoundId: string,
) {
  const queryParams = new URLSearchParams({
    page: page.toString(),
    limit: limit.toString(),
  });

  const url = `${apiEndpoint}/${interviewRoundId}/interview-applicants?${queryParams.toString()}`;
  return fetch(createRequest(url, 'POST', 'application/json', true, body));
}
// utils/api_service/SmartInterview.ts
export async function getInterviewRoundAllApplicants(
  body: AllApplicantRequest,
  page: number = 1,
  limit: number = 10,
  interviewRoundId: string,
): Promise<Response> {
  const queryParams = new URLSearchParams({
    page: page.toString(),
    limit: limit.toString(),
    // Add flags for previous round logic

  });

  const url = `${apiEndpoint}/${interviewRoundId}/applicants/search?${queryParams}`;
  return fetch(createRequest(url, 'POST', 'application/json', true, body));
}

export async function sendInvitations(
  body: ISendInviteRequest,
  interviewRoundId: string,
) {
  const url = `${apiEndpoint}/${interviewRoundId}/applicants`;
  return fetch(createRequest(url, 'POST', 'application/json', true, body));
}

export async function getInterviewRound(interviewId: string) {
  return fetch(
    createRequest(
      apiEndpoint + `/${interviewId}`,
      'GET',
      'application/json',
      true,
    ),
  );
}

export async function generateReportPDF(
  interviewApplicantIds: string[],
  interviewSections: string[],
  interviewRoundId: string,
) {
  const body = {
    interviewApplicantIds: interviewApplicantIds,
    interviewSections: interviewSections,
  };
  return fetch(
    createRequest(
      `${apiEndpoint}/${interviewRoundId}/applicants/report`,
      'POST',
      'application/json',
      true,
      body,
    ),
  );
}

export async function getReport(interviewRoundId: string, reportId: string) {
  return fetch(
    createRequest(
      `${apiEndpoint}/${interviewRoundId}/applicants/report/${reportId}`,
      'GET',
      'application/json',
      true,
    ),
  );
}
